// src/hooks/useAuth.js
import { useState } from "react";
import { AuthService } from "../services/AuthService.service";
import { QuizService } from "../services/Quiz.service";
import { isSubscriptionError } from "../services/Subscription.service";
import { getPhoneErrorKey } from "../utils/phoneValidation";
import { handleApiError } from "../utils/handleApiError";
import { getPendingClaim, clearPendingClaim } from "../utils/pendingClaim";
import { useI18n } from "../i18n";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const useAuth = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    password: "",
    general: "",
  });

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");

  const clearErrors = () => {
    setErrors({ name: "", phone: "", password: "", general: "" });
  };

  const validateRegister = () => {
    let newErrors = { name: "", phone: "", password: "", general: "" };
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = t("validation.name_required");
      isValid = false;
    }

    const phoneErrKey = getPhoneErrorKey(phone);
    if (phoneErrKey) {
      newErrors.phone = t(phoneErrKey);
      isValid = false;
    }

    if (!password) {
      newErrors.password = t("validation.password_required");
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = t("validation.password_min");
      isValid = false;
    }

    if (!isValid) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    }

    clearErrors();
    return true;
  };

  const validateLogin = () => {
    let newErrors = { name: "", phone: "", password: "", general: "" };
    let isValid = true;

    const phoneErrKey = getPhoneErrorKey(phone);
    if (phoneErrKey) {
      newErrors.phone = t(phoneErrKey);
      isValid = false;
    }

    if (!password) {
      newErrors.password = t("validation.password_required");
      isValid = false;
    }

    if (!isValid) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    }

    clearErrors();
    return true;
  };

  /**
   * Uğurlu auth-dan sonra:
   *  1) Qonaq quiz-i bitirib gəlibsə → sessiyanı claim et, kuponu göstər
   *  2) Yönləndirmə saxlanılıbsa → ora qayıt
   *  3) Əks halda → mağaza siyahısı
   */
  const afterAuth = async () => {
    const pending = getPendingClaim();

    if (pending?.sessionId) {
      try {
        const res = await QuizService.claim({
          sessionId: pending.sessionId,
          guestToken: pending.guestToken,
        });
        clearPendingClaim();

        if (res.coupon) {
          toast.success(t("sub.coupon_ready", { code: res.coupon.code }));
          navigate("/coupons");
          return;
        }
        toast.info(t("sub.session_linked"));
      } catch (err) {
        // Abunəlik tələb olunursa sessiya SAXLANILIR — abunə olduqdan sonra
        // "Abunəliyim" səhifəsi kuponu avtomatik alacaq.
        if (isSubscriptionError(err)) {
          toast.info(t("sub.claim_needed"));
          navigate("/subscription");
          return;
        }
        clearPendingClaim();
        toast.error(t("sub.claim_failed"));
      }
    }

    const redirect = sessionStorage.getItem("quizzo_redirect");
    sessionStorage.removeItem("quizzo_redirect");
    navigate(redirect || "/stores");
  };

  const login = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateLogin()) return;

    setLoading(true);
    try {
      const user = await AuthService.login(phone, password);
      if (user) {
        Cookies.set("auth_token", user.token, { expires: 7 });
        if (user.customer?.name) {
          Cookies.set("customer_name", user.customer.name, { expires: 7 });
        }
        await afterAuth();
      }
      clearErrors();
      setName("");
      setPhone("");
      setPassword("");
    } catch (err) {
      setErrors((prev) => ({ ...prev, general: handleApiError(err, t) }));
    } finally {
      setLoading(false);
    }
  };

  const register = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateRegister()) return;

    setLoading(true);
    try {
      const user = await AuthService.register(name, phone, password);
      if (user) {
        Cookies.set("auth_token", user.token, { expires: 7 });
        if (user.customer?.name) {
          Cookies.set("customer_name", user.customer.name, { expires: 7 });
        }
        await afterAuth();
      }
      clearErrors();
      setName("");
      setPhone("");
      setPassword("");
    } catch (err) {
      setErrors((prev) => ({ ...prev, general: handleApiError(err, t) }));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    Cookies.remove("auth_token");
    Cookies.remove("customer_name");
    navigate("/stores");
  };

  return {
    loading,
    errors,
    name,
    password,
    phone,
    setName,
    setPassword,
    setPhone,
    login,
    register,
    logout,
  };
};

export const isLoggedIn = () => Boolean(Cookies.get("auth_token"));
export const customerName = () => Cookies.get("customer_name") || "";
